// The optional "live preview" link on a product: a public page buyers can open
// before they buy, such as a read-only Notion page, a Canva view-only link or a
// demo site. Stored as `details.livePreviewUrl` on the product document, which
// is world-readable, and that is the point: unlike the access links
// (src/lib/product-access-links.ts) this one is meant to be seen.
//
// Two things can go wrong with it, and this module is the gate for both.
//
// 1. It is seller-written text that the product page turns into an href, on a
//    document any client can write a value into for their own product. So the
//    page renders only what resolveLivePreviewUrl returns: an http(s) link that
//    isValidUrl accepts, the same render-time gate the access links get.
//
// 2. A seller who sells a Notion template has one obvious link to paste, and it
//    is the paid one: a Notion page shared with "Allow duplicate as template" is
//    both the preview and the product. Published as the preview, it hands every
//    visitor the template for free. livePreviewIssue refuses a preview that
//    opens the same page as one of the product's access links, however the
//    link is spelled (Notion's ?pvs=4, its workspace URL for the same page, a
//    tracking parameter). The editors show the reason and block the save. It
//    cannot catch a duplicable page that is NOT also an access link, so the
//    editors also tell a seller with a Notion preview to switch duplication off.
//
// Pure: no React, no Firestore.

import { isValidUrl } from './url-validation';
import { MAX_ACCESS_LINK_LENGTH } from './product-access-links';

/** Longest preview link the editors accept: the same limit as an access link. */
export const MAX_LIVE_PREVIEW_URL_LENGTH = MAX_ACCESS_LINK_LENGTH;

/** Why a preview link cannot be saved. */
export type LivePreviewIssue = 'invalid' | 'too-long' | 'access-link';

/** Hosts Notion serves pages from, matched exactly or as a parent domain. */
const NOTION_HOSTS = ['notion.so', 'notion.site', 'notion.com'];

/**
 * Query parameters that only track how a link was shared. Dropped before two
 * links are compared, so `?usp=sharing` or Notion's `?pvs=4` does not make the
 * paid link look like a different page. Every other parameter is kept, because
 * on some sites the query names the resource (Drive's `open?id=`).
 */
const TRACKING_PARAMS = new Set(['pvs', 'usp', 'ref', 'fbclid', 'gclid', 'mc_cid', 'mc_eid', 'si']);

type Doc = Record<string, unknown>;

function isRecord(v: unknown): v is Doc {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function parse(raw: string): URL | null {
  try {
    return new URL(raw.trim());
  } catch {
    return null;
  }
}

function bareHost(url: URL): string {
  return url.hostname.toLowerCase().replace(/^www\./, '');
}

function hostIsNotion(host: string): boolean {
  return NOTION_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
}

/** Whether a link opens a Notion page. */
export function isNotionLink(raw: string): boolean {
  const url = parse(raw);
  return !!url && hostIsNotion(url.hostname.toLowerCase());
}

/**
 * The 32-hex page id at the end of a Notion link's last path segment, or null.
 * Notion writes it after the title slug ("Budget-Planner-<id>"), bare, or as a
 * dashed UUID; with the dashes removed it is the segment's last 32 characters.
 */
function notionPageId(url: URL): string | null {
  const segment = url.pathname.split('/').filter(Boolean).pop() ?? '';
  const match = segment.replace(/-/g, '').match(/[0-9a-f]{32}$/i);
  return match ? match[0].toLowerCase() : null;
}

/** Hosts that address one Google Docs or Drive file by the same id. */
const GOOGLE_FILE_HOSTS = new Set(['docs.google.com', 'drive.google.com', 'sheets.google.com', 'slides.google.com']);

/**
 * The Docs or Drive file id a Google link names, or null. One document has many
 * links: its /edit, /view, /preview and /copy pages (`/d/<id>`, also under
 * `/u/0/`), Drive's `/file/d/<id>`, `open?id=` and `uc?id=`, and a folder's
 * `/drive/folders/<id>` (also under `/drive/u/0/`). Ids are long; a short path
 * segment is not taken for one.
 */
function googleFileId(url: URL): string | null {
  if (!GOOGLE_FILE_HOSTS.has(url.hostname.toLowerCase())) return null;
  const inPath = url.pathname.match(/\/(?:d|folders)\/([A-Za-z0-9_-]{20,})/);
  if (inPath) return inPath[1];
  const id = url.searchParams.get('id');
  return id && /^[A-Za-z0-9_-]{20,}$/.test(id) ? id : null;
}

function canonicalQuery(url: URL): string {
  return [...url.searchParams.entries()]
    .filter(([key]) => !key.toLowerCase().startsWith('utm_') && !TRACKING_PARAMS.has(key.toLowerCase()))
    .map(([key, value]) => `${key}=${value}`)
    .sort()
    .join('&');
}

/**
 * Whether two links open the same page: the same host (ignoring `www.`), the
 * same path (ignoring a trailing slash) and the same query once share-tracking
 * parameters are dropped. The scheme, port and fragment are ignored. Two Notion
 * links compare by the page id they name, and two Google Docs or Drive links by
 * the file id, since one page or file has several URLs and anyone who can view
 * a Google document can copy it.
 */
export function sameLinkTarget(a: string, b: string): boolean {
  const ua = parse(a);
  const ub = parse(b);
  if (!ua || !ub) return a.trim().toLowerCase() === b.trim().toLowerCase();
  const ha = bareHost(ua);
  const hb = bareHost(ub);
  if (hostIsNotion(ha) && hostIsNotion(hb)) {
    const ia = notionPageId(ua);
    const ib = notionPageId(ub);
    if (ia && ib) return ia === ib;
  }
  const ga = googleFileId(ua);
  const gb = googleFileId(ub);
  if (ga && gb) return ga === gb;
  if (ha !== hb) return false;
  if (ua.pathname.replace(/\/+$/, '') !== ub.pathname.replace(/\/+$/, '')) return false;
  return canonicalQuery(ua) === canonicalQuery(ub);
}

/** The preview link a product page may render from a product's `details`, or null. */
export function resolveLivePreviewUrl(details: unknown): string | null {
  if (!isRecord(details) || typeof details.livePreviewUrl !== 'string') return null;
  const url = details.livePreviewUrl.trim();
  if (!url || url.length > MAX_LIVE_PREVIEW_URL_LENGTH) return null;
  return isValidUrl(url) ? url : null;
}

/** The editors' explanation of a preview link problem, under the field and in the launch checklist. */
export function livePreviewMessage(issue: LivePreviewIssue): string {
  switch (issue) {
  case 'invalid':
    return "The live preview link isn't a valid web address. It must start with http:// or https://.";
  case 'too-long':
    return `The live preview link is longer than ${MAX_LIVE_PREVIEW_URL_LENGTH} characters.`;
  case 'access-link':
    return 'The live preview link is one of your access links. Shown on the product page, it would give the product away for free.';
  }
}

/**
 * What stops a seller saving this preview link, or null when nothing does. A
 * blank link is fine: the field is optional.
 */
export function livePreviewIssue(raw: string, accessLinks: readonly unknown[]): LivePreviewIssue | null {
  const url = (raw ?? '').trim();
  if (!url) return null;
  if (url.length > MAX_LIVE_PREVIEW_URL_LENGTH) return 'too-long';
  if (!isValidUrl(url)) return 'invalid';
  const paid = accessLinks.some((link) => typeof link === 'string' && link.trim() !== '' && sameLinkTarget(link, url));
  return paid ? 'access-link' : null;
}
